/* global committedExpendituresCreateUrl uncommittedExpendituresUpdateUrl uncommittedExpendituresPreCommitUrl */
/* global expenseFundApprovalsUpdateUrl department costcenter */
/* global suppliersIndexUrl suppliersStoreUrl suppliersCreateUrl ACCEPTED_ATTACHMENT_FILE_TYPES */
/* global procurementEmailMembers */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';

import classNames from 'classnames';
import { Modal } from 'flowbite';
import { Formik, Field } from 'formik';
import moment from 'moment';
import CreatableSelect from 'react-select/creatable';
import * as Yup from 'yup';

import UncommittedExpendituresEditModal from '../committed_expenditures/UncommittedExpendituresEditModal';
import MilestonesEdit from '../milestones/procurement_milestones/MilestonesEdit';
import DangerAlert from '../partial/alerts/DangerAlert';
import SuppliersCreateModal from '../suppliers/SuppliersCreateModal';

import CommitmentLinesTable from './CommitmentLinesTable';
import RequestSupplierModal from './RequestSupplierModal';
import UncommittedImpactHeader from './UncommittedImpactHeader';

import { idempotencyHeader } from 'Helpers/apiUtils';
import { buildLockedRecipients, buildMemberOptions } from 'Helpers/approvalRecipients';
import { modalOptions } from 'Helpers/modalOptions';
import { committingTotals, releasedOnApprovalTotals, uncommittedNowTotals } from 'Helpers/procurementUtils';

import Alert from 'CommonComponents/Alert';
import Button from 'CommonComponents/Button';
import CheckboxFormField from 'CommonComponents/CheckboxFormField';
import DatePickerField from 'CommonComponents/DatePickerField';
import LockedRecipientChips from 'CommonComponents/LockedRecipientChips';
import SelectField from 'CommonComponents/SelectField';
import Tooltip from 'CommonComponents/Tooltip';
import UserSelectorInput from 'CommonComponents/UserSelectorInput';

import { useToast } from 'Hooks/useToast';
import { useUsers } from 'Hooks/useUsers';

import PlusCircleIcon from 'Assets/icons/plus-circle.svg';

// owner_id is defaulted to the current user and is never empty, so it is NOT required.
// description is optional. supplier_id is the only hard-required field here; the
// committing amount and milestone validity are gated separately on the submit button.
const OrderCreateSchema = Yup.object().shape({
    owner_id: Yup.string(),
    description: Yup.string(),
    supplier_id: Yup.string().required($t('This value is required')),
    contract_id: Yup.string(),
    contract_started_at: Yup.string(),
    contract_ended_at: Yup.string(),
    contract_closed: Yup.boolean(),
    contract_signed: Yup.boolean(),
});

export default function DirectCommitForm(props) {
    const [user, setUser] = useState({});
    const [submissionDate, setSubmissionDate] = useState('');
    const [suppliers, setSuppliers] = useState([]);
    const [suppliersLoading, setSuppliersLoading] = useState(true);

    const [lineItems, setLineItems] = useState([]);
    const [lineItemsLoading, setLineItemsLoading] = useState(true);
    const [selectedFiles, setSelectedFiles] = useState([]);

    // The row currently being edited via the per-type edit modal (empty object = none).
    const [editingLine, setEditingLine] = useState({});

    // Bumped after every successful line edit so MilestonesEdit re-derives its draft live.
    const [milestoneReloadSignal, setMilestoneReloadSignal] = useState(0);

    // Milestone section validity (cash / vik) lifted from MilestoneSectionForm.
    const [sectionValidity, setSectionValidity] = useState({});
    const [presentSections, setPresentSections] = useState([]);
    // Pending milestone autosave state per section, lifted from MilestoneSectionForm,
    // so a fast commit cannot race an unsaved draft.
    const [milestoneSaving, setMilestoneSaving] = useState({});

    const [total, setTotal] = useState(0);
    const [variance, setVariance] = useState(0);

    const [createNewSupplier, setCreateNewSupplier] = useState(false);
    const [error, setError] = useState(null);
    // Stays true from the click until the redirect, so the form never looks idle once a commit is accepted.
    const [commitRunning, setCommitRunning] = useState(false);
    // Why the last commit did not go through, from the server's message or the request failing outright.
    const [commitError, setCommitError] = useState(null);
    const [dcRecipients, setDcRecipients] = useState(props.dcDefaultRecipients || []);

    const [suppliersCreateModalElement, setSuppliersCreateModalElement] = useState(null);
    const suppliersCreateModalRef = useRef();

    // Supplier-request ("Request new supplier to be added") modal + awaiting state (IB-610).
    const [requestSupplierModalElement, setRequestSupplierModalElement] = useState(null);
    const requestSupplierModalRef = useRef();
    // Seed from any open request already on the draft group so the awaiting/ambiguous area
    // renders on load (AC9), not only right after a fresh request is submitted.
    const [supplierRequest, setSupplierRequest] = useState(props.existingSupplierRequest || null);
    const [resolvingRequest, setResolvingRequest] = useState(false);

    const [editModalElement, setEditModalElement] = useState(null);
    const editModalRef = useRef();

    const uncommittedExpenditureGroupId = props.uncommittedExpenditureGroupId;

    // Tracks the latest Formik values so the supplier-request modal (rendered outside the
    // Formik render-prop) can persist the current draft before sending the request (IB-610).
    const latestValuesRef = useRef(null);
    // Lets the out-of-tree request modal set the supplier field when the user picks an existing
    // supplier from the type-ahead suggestions instead of requesting a new one (IB-610 stretch).
    const setFieldValueRef = useRef(null);

    const toast = useToast();
    const { users } = useUsers();

    const suppliersOptions = useMemo(() => suppliers.map((supplier) => ({ value: supplier.id, label: supplier.name })), [suppliers]);

    // Recipients configured in Commitment Settings; the request button is gated on having at least one (IB-610).
    const supplierRequestRecipients = props.supplierRequestRecipients || [];
    const hasRecipients = supplierRequestRecipients.length > 0;

    // The Requestor defaults to the logged-in user, but the event user list (useUsers) does not always
    // contain them (e.g. a super-admin who is not an event member). Merge the current user in so they are
    // always a selectable option and the pre-selected owner_id can resolve to a visible label.
    const requestorUsers = useMemo(() => {
        if (user && user.id && !users.some((u) => u.id === user.id)) {
            return [...users, user];
        }
        return users;
    }, [users, user]);

    // Workspace-member options for the recipient picker, sourced from the Blade-injected
    // global (array of { name, email }) so all three pickers stay consistent. The approver
    // can pick a member or type an arbitrary email (creatable free-text).
    const members = typeof procurementEmailMembers !== 'undefined' ? procurementEmailMembers : [];
    const memberOptions = useMemo(() => buildMemberOptions(members), [members]);

    useEffect(() => {
        if (createNewSupplier) {
            suppliersCreateModalElement && suppliersCreateModalElement.show();
        }
    }, [createNewSupplier]);

    useEffect(() => {
        fetchData();
        fetchLineItems();
        setSubmissionDate(moment().format('DD/MM/YYYY'));
        suppliersCreateModalRef && setSuppliersCreateModalElement(new Modal(suppliersCreateModalRef.current, modalOptions(false)));
        editModalRef && setEditModalElement(new Modal(editModalRef.current, modalOptions(false)));
        requestSupplierModalRef && setRequestSupplierModalElement(new Modal(requestSupplierModalRef.current, modalOptions(false)));
    }, []);

    // Open the per-type edit modal whenever a row is selected for editing.
    useEffect(() => {
        if (Object.keys(editingLine).length > 0) {
            editModalElement && editModalElement.show();
        }
    }, [editingLine]);

    // The SINGLE committing-totals computation for the whole screen. committingTotals() walks the
    // lineItems tree and sums the committing cash / VIK of every committable LEAF exactly once —
    // recursing through a project's nested template / pack / SBL members and never counting a
    // container's own aggregate. The impact headline, the commitment table footer and the
    // milestone Cash / VIK value base are all fed from this so they can never diverge.
    const committing = useMemo(() => committingTotals(lineItems), [lineItems]);

    // Recompute the running total + variance from the single committing source. `total` is the
    // committing grand total (cash + VIK) used by the footer Total column and the submit gate.
    useEffect(() => {
        const runningTotal = committing.cash + committing.vik;

        const budgetedTotal = lineItems.reduce((sum, item) => {
            const budgeted = item.originalCashflowObject?.uncommitted ?? item.originalCashflowObject?.total ?? item.cashflow_object?.total ?? 0;
            return sum + budgeted;
        }, 0);

        setTotal(runningTotal);
        setVariance(runningTotal - budgetedTotal);
    }, [lineItems, committing]);

    const fetchData = () => {
        setSuppliersLoading(true);
        axios
            .get(props.refreshCommitmentDataUrl, { params: { fields: 'slim' } })
            .then((response) => {
                const { data } = response;
                setUser({ ...data.user });
                setSuppliers([...data.suppliers]);
            })
            .catch((e) => console.log(e))
            .finally(() => setSuppliersLoading(false));
    };

    const fetchLineItems = () => {
        if (!uncommittedExpenditureGroupId) {
            setLineItemsLoading(false);
            return;
        }

        setLineItemsLoading(true);
        let url = committedExpendituresCreateUrl;
        if (url.includes('UNCOMMITTED_EXPENDITURE_GROUP_ID')) {
            url = url.replace('UNCOMMITTED_EXPENDITURE_GROUP_ID', uncommittedExpenditureGroupId);
        } else {
            url += '?uncommittedExpenditureGroupId=' + uncommittedExpenditureGroupId;
        }

        axios
            .get(url)
            .then((res) => {
                const items = res.data || [];
                setLineItems(items);
            })
            .catch((e) => {
                console.log(e);
                setLineItems([]);
            })
            .finally(() => setLineItemsLoading(false));
    };

    // Open the per-type edit modal for a row. The modal (UncommittedExpendituresEditModal)
    // dispatches internally by expense type/assign to the correct existing edit UI:
    //   - expense + assign 'event'  → UncommittedExpendituresEditForm (simple / SBL, incl. project members)
    //   - expense + assign 'venue'  → TemplateAssign (venue-assigned)
    //   - template                  → TemplateAssign (selectable)
    //   - pack                      → PackTable
    const handleEditLine = (item) => {
        setEditingLine(item);
    };

    // SBL guard the edit form uses to clamp project-member committing amounts to the
    // member's allocated amount. Mirrors the old wizard's isProjectSblExpense().
    const isProjectSblExpense = (item) => !!item && item.expense?.project_id !== null && item.expense?.type === 'expense';

    const editModalCloseHandler = () => {
        editModalElement && editModalElement.hide();
        setEditingLine({});
    };

    // After a modal save: hide the modal, re-fetch the lines so the committing display,
    // impact headline and running total all re-derive from the persisted UE amount_data,
    // and re-derive the milestone draft (milestones depend on the leaf UE amounts).
    const editModalSuccessHandler = () => {
        editModalElement && editModalElement.hide();
        setEditingLine({});
        fetchLineItems();
        // Milestones are derived from the leaf UE amounts, so re-derive the draft live
        // once the edited amounts are persisted (not only on mount).
        setMilestoneReloadSignal((prev) => prev + 1);
    };

    const handleMilestoneSavingChange = (section, isSaving) => {
        setMilestoneSaving((prev) => (prev[section] === isSaving ? prev : { ...prev, [section]: isSaving }));
    };

    const handleValidityChange = (section, isValid) => {
        setSectionValidity((prev) => (prev[section] === isValid ? prev : { ...prev, [section]: isValid }));
    };

    const handleSectionPresent = (section) => {
        setPresentSections((prev) => (prev.includes(section) ? prev : [...prev, section]));
    };

    const changeFilesHandler = (event) => {
        setSelectedFiles(event.target.files);
    };

    // AC9 — resolve an ambiguous supplier request from the awaiting area. Confirm links the
    // chosen candidate supplier (and pre-selects it here); "Keep waiting" rejects the request
    // so it drops out of auto-link, clearing the awaiting banner.
    const buildResolveUrl = (template) => (template && supplierRequest ? template.replace('SUPPLIER_REQUEST_ID', supplierRequest.id) : '');

    const handleConfirmCandidate = (supplierId, setFieldValue) => {
        if (!supplierRequest || !supplierId) {
            return;
        }
        setResolvingRequest(true);
        axios
            .post(buildResolveUrl(props.supplierRequestConfirmUrl), { supplier_id: supplierId }, { headers: { ...idempotencyHeader() } })
            .then(() => {
                setFieldValue && setFieldValue('supplier_id', supplierId);
                setSupplierRequest(null);
                fetchData();
                toast.success($t('Supplier linked'));
            })
            .catch(() => toast.error($t('An error occurred, please try again')))
            .finally(() => setResolvingRequest(false));
    };

    const handleKeepWaiting = () => {
        if (!supplierRequest) {
            return;
        }
        setResolvingRequest(true);
        axios
            .post(buildResolveUrl(props.supplierRequestRejectUrl), {}, { headers: { ...idempotencyHeader() } })
            .then(() => {
                setSupplierRequest(null);
                toast.success($t('Request dismissed'));
            })
            .catch(() => toast.error($t('An error occurred, please try again')))
            .finally(() => setResolvingRequest(false));
    };

    // Finding #1 — the requestor found the supplier already exists and picked it from the
    // dropdown while the draft is still awaiting. Withdraw the open request (any open status):
    // links the chosen supplier to the draft, clears the awaiting banner so submit unblocks, and
    // emails the configured recipients a "no longer required" follow-up so AP skip the duplicate.
    const handleWithdrawForSupplier = (supplierId) => {
        if (!supplierRequest || !supplierId || !props.supplierRequestWithdrawUrl) {
            return;
        }
        setResolvingRequest(true);
        axios
            .post(buildResolveUrl(props.supplierRequestWithdrawUrl), { supplier_id: supplierId }, { headers: { ...idempotencyHeader() } })
            .then(() => {
                setSupplierRequest(null);
                toast.success($t('Supplier selected — request withdrawn'));
            })
            .catch(() => toast.error($t('An error occurred, please try again')))
            .finally(() => setResolvingRequest(false));
    };

    // Cancel: remove the auto-created draft group + draft milestones, then redirect.
    const handleCancel = () => {
        const redirect = () => {
            window.location.href = props.redirectUrl && typeof props.redirectUrl != 'undefined' ? props.redirectUrl : props.procurementsDashboardUrl;
        };

        if (!props.draftDestroyUrl) {
            redirect();
            return;
        }

        axios
            .delete(props.draftDestroyUrl, { headers: { ...idempotencyHeader() } })
            .catch((e) => console.log(e))
            .finally(redirect);
    };

    // Persist the in-progress draft metadata (IB-610). Does NOT commit — usable while a
    // supplier request is still open. Returns the axios promise so callers (e.g. the request
    // modal's "Send request & save draft") can chain off a successful save.
    const handleSaveDraft = (values, { silent = false } = {}) => {
        if (!props.draftSaveUrl) {
            return Promise.resolve();
        }

        setError(null);
        setCommitError(null);

        const payload = {
            uncommittedExpenditureGroupId: uncommittedExpenditureGroupId ?? null,
            owner_id: values?.owner_id ?? null,
            description: values?.description ?? null,
            supplier_id: values?.supplier_id || null,
            contract_id: values?.contract_id || null,
            contract_started_at: values?.contract_started_at ? moment(values.contract_started_at).format('YYYY-MM-DD') : null,
            contract_ended_at: values?.contract_ended_at ? moment(values.contract_ended_at).format('YYYY-MM-DD') : null,
            contract_signed: values?.contract_signed ? true : false,
            contract_closed: values?.contract_closed ? true : false,
        };

        return axios
            .post(props.draftSaveUrl, payload, { headers: { ...idempotencyHeader() } })
            .then((response) => {
                if (!silent) {
                    toast.success($t('Draft saved'));
                }
                return response;
            })
            .catch((e) => {
                console.log(e);
                setError(e?.response?.data?.errors);
                // A draft already committed from another window is refused with a message only.
                if (e?.response?.status === 409 && typeof e.response.data?.message === 'string') {
                    setCommitError(e.response.data.message);
                }
                throw e;
            });
    };

    // Build the cash / VIK split for the header. Both sides use the SAME leaf traversal as the
    // committing-totals source so they never diverge: "This commitment" = committingTotals, and
    // "Uncommitted now" = uncommittedNowTotals (each committable leaf's pre-commit balance, split
    // cash / VIK — correctly recursing into project members rather than iterating top-level only).
    // "released" is the saving that leaves the lines when the commitment is approved, which
    // "Uncommitted after" takes off too.
    const impact = useMemo(
        () => ({ now: uncommittedNowTotals(lineItems), committing, released: releasedOnApprovalTotals(lineItems) }),
        [lineItems, committing],
    );

    const lineContext = useMemo(() => {
        const first = lineItems[0];
        const dept = first?.expense?.department?.name ?? props.departmentName;
        const cc = first?.expense?.costcentre?.name ?? first?.expense?.costcenter?.name ?? props.costcentreName;
        const crumbs = [dept, cc].filter(Boolean).join(' › ');
        const line = first?.expense?.name ?? first?.name ?? '';
        return { crumbs, line };
    }, [lineItems, props.departmentName, props.costcentreName]);

    const overCommitted = impact.now.cash + impact.now.vik < impact.committing.cash + impact.committing.vik;

    // All present milestone sections must total 100%. Scope every read to presentSections so a
    // stale validity/saving key left behind by a section that was dropped on a reload can never
    // gate the button.
    const milestonesValid = presentSections.length === 0 || presentSections.every((section) => sectionValidity[section] === true);

    // Block the commit while any CURRENTLY-present milestone section has a pending (debounced)
    // autosave in flight, so a fast click can never create a PO whose draft milestones were never
    // persisted — and a stale saving:true for a dropped section cannot keep the button disabled.
    const milestoneSaveInFlight = presentSections.some((section) => milestoneSaving[section] === true);

    const submitDisabled = total <= 0 || milestonesValid !== true || milestoneSaveInFlight;

    // Render the Requestor / Submission Date block into the page header (right of the page title) via a
    // portal so it stays inside the Formik tree (live owner_id binding) while appearing in the header.
    // Falls back to an inline, right-aligned block if the header mount point is not present.
    const renderHeaderMeta = (content) => {
        const target = typeof document !== 'undefined' ? document.getElementById('direct-commit-header-meta') : null;
        if (target) {
            return ReactDOM.createPortal(content, target);
        }
        return <div className="tw-flex tw-justify-end tw-p-6 tw-border-b tw-border-charcoal-15">{content}</div>;
    };

    return (
        <div className="tw-pb-8">
            <Formik
                initialValues={{
                    // Rehydrate from a previously saved draft (IB-610) when present, falling
                    // back to the existing defaults for a freshly scaffolded group.
                    owner_id: props.draftData?.owner_id ?? props.currentUserId ?? '',
                    description: props.draftData?.description ?? '',
                    // Pre-select the supplier auto-linked onto the draft group by a resolved
                    // supplier request, so the user does not re-select it at commit (IB-610 C2).
                    supplier_id: props.groupSupplierId ?? '',
                    contract_id: props.draftData?.contract_id ?? '',
                    contract_started_at: props.draftData?.contract_started_at ?? '',
                    contract_ended_at: props.draftData?.contract_ended_at ?? '',
                    contract_closed: props.draftData?.contract_closed ?? false,
                    contract_signed: props.draftData?.contract_signed ?? false,
                }}
                validationSchema={OrderCreateSchema}
                onSubmit={(values, actions) => {
                    let data = { ...values };
                    setError(null);
                    setCommitError(null);
                    setCommitRunning(true);

                    const formData = new FormData();
                    formData.append('owner_id', data.owner_id ?? '');
                    formData.append('description', data.description ?? '');
                    formData.append('final_price', '' + total);
                    formData.append('variance', '' + variance);
                    formData.append('lineItems', JSON.stringify(lineItems));
                    formData.append('uncommittedExpenditureGroupId', uncommittedExpenditureGroupId ?? '');
                    formData.append('supplier_id', data.supplier_id);
                    if (data.contract_id) {
                        formData.append('contract_id', data.contract_id);
                    }
                    formData.append('contract_started_at', data.contract_started_at ? moment(data.contract_started_at).format('YYYY-MM-DD') : '');
                    formData.append('contract_ended_at', data.contract_ended_at ? moment(data.contract_ended_at).format('YYYY-MM-DD') : '');
                    formData.append('contract_signed', data.contract_signed ? '1' : '0');
                    formData.append('contract_closed', data.contract_closed ? '1' : '0');

                    if (props.dcEmailEnabled) {
                        (dcRecipients || []).forEach((email, index) => {
                            formData.append(`additional_recipients[${index}]`, email);
                        });
                    }

                    for (let i = 0; i < selectedFiles.length; i++) {
                        formData.append(`files[${i}]`, selectedFiles[i]);
                    }

                    axios
                        .post(props.storeUrl, formData, { headers: { ...props.mutationHeader } })
                        .then(({ data: result }) => {
                            toast.success(result?.status === 'awaiting_approval' ? $t('Commitment submitted for approval') : $t('Commitment completed'));
                            setTimeout(
                                () =>
                                    (window.location.href =
                                        props.redirectUrl && typeof props.redirectUrl != 'undefined' ? props.redirectUrl : props.procurementsDashboardUrl),
                                2000,
                            );
                        })
                        .catch((e) => {
                            console.log(e);
                            setCommitRunning(false);

                            const status = e?.response?.status;
                            const body = e?.response?.data;
                            const isJson = !!body && typeof body === 'object';

                            if (status === 413) {
                                setCommitError($t('The attachments are too large to upload. Remove or shrink them and try again.'));
                            } else if (isJson && body.errors) {
                                setError(body.errors);
                                actions.setErrors(body.errors);
                            } else if (isJson && typeof body.message === 'string' && body.message && (body.outcome || status < 500)) {
                                // A 5xx is only trusted when the commit itself says nothing was
                                // written; a generic server error can come from a fatal error after
                                // the commit landed.
                                setCommitError(body.message);
                            } else {
                                // No response, a gateway error or a server error that is not the
                                // commit's own: the request may have been cut off after it landed.
                                setCommitError(
                                    $t("We couldn't confirm whether the commitment was saved. Refresh the procurement to check before trying again."),
                                );
                            }
                        })
                        .finally(() => actions.setSubmitting(false));
                }}
            >
                {({ values, errors, touched, isSubmitting, handleSubmit, setFieldValue }) => {
                    // Keep the latest values available to the out-of-tree supplier-request modal (IB-610).
                    latestValuesRef.current = values;
                    setFieldValueRef.current = setFieldValue;

                    const contractsOptions = useMemo(() => {
                        const supplier = suppliers.find((sup) => sup.id == values.supplier_id);
                        let options = [{ value: '', label: $t('New Contract') }];
                        options = options.concat(
                            supplier?.supplier_contract.map((contract) => ({
                                value: contract.id,
                                label: contract.contract_reference,
                            })) || [],
                        );
                        return options;
                    }, [values.supplier_id, suppliers]);

                    return (
                        <div className="card front bg-white border-0 rounded-15px shadow tw-overflow-hidden">
                            {/* Requestor + submission date — portaled into the page header (right of the page title)
                                so they sit alongside the page "Direct Commitment" title rather than repeating it here. */}
                            {renderHeaderMeta(
                                <div className="tw-flex tw-items-start tw-gap-6">
                                    <div>
                                        <div className="tw-text-xs tw-uppercase tw-tracking-wide tw-text-charcoal-50 tw-font-semibold tw-mb-1">
                                            {$t('Requestor')}
                                        </div>
                                        <div className="tw-min-w-[12rem]">
                                            <UserSelectorInput
                                                selectedId={values.owner_id}
                                                usersList={requestorUsers}
                                                onChange={(value) => setFieldValue('owner_id', value)}
                                                fetchUrl={props.listUsersUrl}
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <div className="tw-text-xs tw-uppercase tw-tracking-wide tw-text-charcoal-50 tw-font-semibold tw-mb-1">
                                            {$t('Submission Date')}
                                        </div>
                                        <div className="tw-text-sm tw-font-semibold tw-text-charcoal tw-pt-2">{submissionDate}</div>
                                    </div>
                                </div>,
                            )}

                            {/* Hero: uncommitted impact */}
                            <div className="tw-p-6 tw-border-b tw-border-charcoal-15">
                                <UncommittedImpactHeader
                                    context={lineContext}
                                    now={impact.now}
                                    committing={impact.committing}
                                    released={impact.released}
                                    vikEnabled={props.vikEnabled}
                                />
                            </div>

                            {/* Body: two columns */}
                            <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-6 tw-p-6">
                                {/* LEFT: commitment lines + milestones */}
                                <div className="tw-flex tw-flex-col tw-gap-y-6">
                                    <section>
                                        <h3 className="tw-text-xs tw-uppercase tw-tracking-wide tw-font-bold tw-text-charcoal tw-mb-3">
                                            {$t('Commitment')}{' '}
                                            <span className="tw-text-charcoal-50 tw-font-medium tw-normal-case tw-tracking-normal">
                                                — {$t('line pre-loaded, edit to change amount')}
                                            </span>
                                        </h3>
                                        {lineItemsLoading ? (
                                            <div className="tw-text-sm tw-text-charcoal-50">{$t('Loading')}...</div>
                                        ) : (
                                            <CommitmentLinesTable
                                                lineItems={lineItems}
                                                departmentName={props.departmentName}
                                                costcentreName={props.costcentreName}
                                                vikEnabled={props.vikEnabled}
                                                committingTotals={committing}
                                                onEdit={handleEditLine}
                                            />
                                        )}
                                    </section>

                                    <section>
                                        <h3 className="tw-text-xs tw-uppercase tw-tracking-wide tw-font-bold tw-text-charcoal tw-mb-3">
                                            {$t('Payment Milestones')}{' '}
                                            <span className="tw-text-charcoal-50 tw-font-medium tw-normal-case tw-tracking-normal">
                                                — {$t('inherited from budget, editable')}
                                            </span>
                                        </h3>
                                        {/* hideSectionHeader is conditional here, unlike the cash-only commit
                                            screens: with VIK enabled two milestone sections render, and the
                                            per-section heading is the only thing naming Cash vs VIK. */}
                                        <MilestonesEdit
                                            getMilestonesUrl={props.getExpenseMilestonesUrl}
                                            minDate={props.minDate}
                                            maxDate={props.maxDate}
                                            parentSection="direct-commit"
                                            hideSectionHeader={!props.vikEnabled}
                                            totalValue={total}
                                            committingCash={committing.cash}
                                            committingVik={committing.vik}
                                            reloadSignal={milestoneReloadSignal}
                                            vikEnabled={props.vikEnabled}
                                            isSliderAllowed={false}
                                            allowConfirm={false}
                                            preserveDraftMilestones={true}
                                            onValidityChange={handleValidityChange}
                                            onSectionPresent={handleSectionPresent}
                                            onAutosaveStateChange={handleMilestoneSavingChange}
                                        />
                                    </section>
                                </div>

                                {/* RIGHT: supplier + description + attachments */}
                                <div className="tw-flex tw-flex-col tw-gap-y-6">
                                    <section>
                                        <h3 className="tw-text-xs tw-uppercase tw-tracking-wide tw-font-bold tw-text-charcoal tw-mb-3">{$t('Supplier')}</h3>
                                        <div className="tw-space-y-4">
                                            <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                                <label htmlFor="supplier_id" className="text-input__label">
                                                    {$t('Recommended Supplier')} <span className="tw-text-red tw-text-sm">*</span>
                                                </label>
                                                {/* Keep the select and the button on one line: the select can shrink (min-w-0)
                                                    while the button never wraps or drops (shrink-0 + whitespace-nowrap). */}
                                                <div className="tw-flex tw-flex-nowrap tw-items-center tw-gap-x-2">
                                                    <div className="tw-grow tw-min-w-0">
                                                        <SelectField
                                                            className="tw-block tw-w-full tw-rounded-lg tw-text-sm !tw-bg-grey/50 tw-text-charcoal tw-font-semibold"
                                                            options={suppliersOptions}
                                                            value={values.supplier_id}
                                                            backgroundColor="#e4e4e480"
                                                            placeholder={$t('Please select')}
                                                            isLoading={suppliersLoading}
                                                            onChange={(supplier) => {
                                                                const supplierId = supplier?.value || '';
                                                                setFieldValue('supplier_id', supplierId);
                                                                setFieldValue('contract_id', '');
                                                                // Picking an existing supplier while awaiting supersedes the open request (IB-610 #1).
                                                                if (supplierId && supplierRequest) {
                                                                    handleWithdrawForSupplier(supplierId);
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                    {!props.integrationConfigured && (
                                                        <Button
                                                            variant="success"
                                                            className="!tw-h-9 tw-shrink-0 tw-whitespace-nowrap"
                                                            onClick={() => setCreateNewSupplier(true)}
                                                        >
                                                            <PlusCircleIcon className="tw-w-3.5 tw-h-3.5 tw-me-2" />
                                                            {$t('Add new Supplier')}
                                                        </Button>
                                                    )}
                                                    {/* ERP-connected events request the new supplier from finance instead of creating it locally (IB-610).
                                                        Gated on supplier sync being ENABLED for the event, not merely configured (I6).
                                                        Coerce to boolean: the blade injects this as 0/1, and `{0 && …}` would leak a
                                                        literal "0" into the DOM next to the Add-supplier button on non-ERP events. */}
                                                    {!!props.syncSuppliersEnabled && (
                                                        <Tooltip
                                                            content={hasRecipients ? '' : $t('Add supplier request recipients in Commitment Settings first.')}
                                                        >
                                                            <Button
                                                                variant="success"
                                                                className="!tw-h-9 tw-shrink-0 tw-whitespace-nowrap"
                                                                disabled={!hasRecipients || !!supplierRequest}
                                                                onClick={() => requestSupplierModalElement && requestSupplierModalElement.show()}
                                                            >
                                                                <PlusCircleIcon className="tw-w-3.5 tw-h-3.5 tw-me-2" />
                                                                {$t('Request new supplier to be added')}
                                                            </Button>
                                                        </Tooltip>
                                                    )}
                                                </div>
                                                {touched.supplier_id && errors.supplier_id && (
                                                    <div className="tw-text-red tw-text-sm">{errors.supplier_id}</div>
                                                )}
                                                {supplierRequest && supplierRequest.status !== 'ambiguous' && (
                                                    <div className="tw-mt-2 tw-rounded-lg tw-border tw-border-amber-400 tw-bg-amber-50 tw-p-3 tw-text-sm tw-text-amber-800 tw-space-y-1">
                                                        <div>
                                                            {$t(
                                                                "Awaiting supplier — {{name}} requested {{date}}. You'll be notified once it's available in the ERP.",
                                                                {
                                                                    name: supplierRequest.requested_name,
                                                                    date: moment(supplierRequest.created_at).format('DD/MM/YYYY'),
                                                                },
                                                            )}
                                                        </div>
                                                        {/* Finding #1 — let the requestor self-serve if the supplier already exists. */}
                                                        <div className="tw-text-xs tw-text-amber-700">
                                                            {$t('Already in the list? Select the supplier above to use it and cancel this request.')}
                                                        </div>
                                                    </div>
                                                )}
                                                {/* AC9 — ambiguous request: surface the candidate supplier(s) for the requestor to
                                                    confirm, or keep waiting (which rejects the request and clears the banner). */}
                                                {supplierRequest && supplierRequest.status === 'ambiguous' && (
                                                    <div className="tw-mt-2 tw-rounded-lg tw-border tw-border-amber-400 tw-bg-amber-50 tw-p-3 tw-text-sm tw-text-amber-800 tw-space-y-2">
                                                        <div>
                                                            {$t(
                                                                'Multiple suppliers match "{{name}}". Confirm the correct one, or keep waiting for a new ERP supplier.',
                                                                { name: supplierRequest.requested_name },
                                                            )}
                                                        </div>
                                                        <ul className="tw-space-y-1.5">
                                                            {(supplierRequest.candidates || []).map((candidate) => (
                                                                <li key={candidate.value} className="tw-flex tw-items-center tw-justify-between tw-gap-x-2">
                                                                    <span className="tw-font-semibold">{candidate.label}</span>
                                                                    <Button
                                                                        type="button"
                                                                        variant="primary"
                                                                        className="!tw-h-8 tw-shrink-0"
                                                                        disabled={resolvingRequest}
                                                                        onClick={() => handleConfirmCandidate(candidate.value, setFieldValue)}
                                                                    >
                                                                        {$t('Confirm')}
                                                                    </Button>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                        <div className="tw-flex tw-justify-end">
                                                            <Button
                                                                type="button"
                                                                variant="secondary"
                                                                className="!tw-h-8"
                                                                disabled={resolvingRequest}
                                                                onClick={handleKeepWaiting}
                                                            >
                                                                {$t('Keep waiting')}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {values.supplier_id && (
                                                <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                                    <label htmlFor="contract_id" className="text-input__label">
                                                        {$t('Contract Reference')}
                                                    </label>
                                                    <SelectField
                                                        className="tw-block tw-w-full tw-rounded-lg tw-text-sm !tw-bg-grey/50 tw-text-charcoal tw-font-semibold"
                                                        options={contractsOptions}
                                                        value={values.contract_id}
                                                        backgroundColor="#e4e4e480"
                                                        placeholder={$t('Please select')}
                                                        onChange={(contract) => setFieldValue('contract_id', contract?.value || '')}
                                                    />
                                                </div>
                                            )}

                                            <div className="tw-grid tw-grid-cols-2 tw-gap-x-3">
                                                <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                                    <label className="text-input__label">{$t('Contract Start Date')}</label>
                                                    <DatePickerField
                                                        name="contract_started_at"
                                                        valueFormat="yyyy-MM-dd"
                                                        dateFormat="yyyy-MM-dd"
                                                        className={classNames('text-input !tw-bg-grey/50 tw-w-full', 'tw-border-0')}
                                                        wrapperClassName="tw-block tw-w-full"
                                                        disabled={isSubmitting}
                                                        value={values.contract_started_at}
                                                        onChange={setFieldValue}
                                                        wrapped
                                                    />
                                                </div>
                                                <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                                    <label className="text-input__label">{$t('Contract End Date')}</label>
                                                    <DatePickerField
                                                        name="contract_ended_at"
                                                        valueFormat="yyyy-MM-dd"
                                                        dateFormat="yyyy-MM-dd"
                                                        className={classNames('text-input !tw-bg-grey/50 tw-w-full', 'tw-border-0')}
                                                        wrapperClassName="tw-block tw-w-full"
                                                        disabled={isSubmitting}
                                                        value={values.contract_ended_at}
                                                        onChange={setFieldValue}
                                                        wrapped
                                                    />
                                                </div>
                                            </div>

                                            <div className="tw-flex tw-gap-x-8">
                                                <CheckboxFormField name="contract_signed" id="contract_signed" type="checkbox" label={$t('Contract Signed')} />
                                                <CheckboxFormField name="contract_closed" id="contract_closed" type="checkbox" label={$t('Contract Closed')} />
                                            </div>
                                        </div>
                                    </section>

                                    <section>
                                        <h3 className="tw-text-xs tw-uppercase tw-tracking-wide tw-font-bold tw-text-charcoal tw-mb-3">
                                            {$t('Expenditure description')}{' '}
                                            <span className="tw-text-charcoal-50 tw-font-medium tw-normal-case tw-tracking-normal tw-bg-charcoal-15 tw-rounded tw-px-1.5 tw-py-0.5">
                                                {$t('Optional')}
                                            </span>
                                        </h3>
                                        <Field
                                            as="textarea"
                                            className="form-control !tw-bg-grey/50 tw-border-0 tw-rounded-lg"
                                            name="description"
                                            rows={3}
                                            placeholder={$t('Please provide an overview of the expenditure you are seeking approval for here')}
                                        />
                                    </section>

                                    <section>
                                        <h3 className="tw-text-xs tw-uppercase tw-tracking-wide tw-font-bold tw-text-charcoal tw-mb-3">{$t('Attachments')}</h3>
                                        <p className="tw-text-xs tw-text-charcoal-50 tw-mb-2">{$t('Attachments are added when you commit, not on a draft.')}</p>
                                        <input
                                            id="purchase-order-attachments"
                                            type="file"
                                            name="files[]"
                                            multiple
                                            className="file-input"
                                            placeholder={$t('Select Files')}
                                            accept={ACCEPTED_ATTACHMENT_FILE_TYPES}
                                            onChange={changeFilesHandler}
                                        />
                                    </section>
                                </div>
                            </div>

                            {/* Over-commit warning banner (does NOT block submit) */}
                            {overCommitted && (
                                <div className="tw-mx-6 tw-mb-2 tw-rounded-lg tw-border tw-border-red-25 tw-bg-red-25/30 tw-px-4 tw-py-3 tw-text-sm tw-text-red tw-font-medium">
                                    {$t('You are committing more than the remaining uncommitted balance. This will result in a negative balance.')}
                                </div>
                            )}

                            {!!error && _.isObject(error) && (
                                <div className="tw-px-6">
                                    {Object.values(error)[0].map((e, i) => (
                                        <DangerAlert key={i} message={e} />
                                    ))}
                                </div>
                            )}

                            {!!commitError && (
                                <div className="tw-px-6 tw-pb-4">
                                    <Alert type="error">{commitError}</Alert>
                                </div>
                            )}

                            {/* Action bar: single commit action */}
                            <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-p-6 tw-border-t tw-border-charcoal-15 tw-bg-charcoal-10">
                                <p className="tw-text-xs tw-text-charcoal-50 tw-max-w-xl tw-mb-0">
                                    {$t('The full audit trail — user, timestamp, prior and new uncommitted — is captured on submit.')}
                                </p>
                                {!!props.dcEmailEnabled && (
                                    <div className="tw-w-full tw-space-y-1.5 tw-mb-3">
                                        <label className="text-input__label">{$t('Email recipients')}</label>
                                        <p className="tw-text-charcoal tw-text-sm tw-mb-0">{$t('Requestor is included automatically.')}</p>
                                        <LockedRecipientChips
                                            recipients={buildLockedRecipients({
                                                requestor: requestorUsers.find((u) => `${u.id}` === `${values.owner_id}`),
                                                requestorRole: $t('requestor'),
                                            })}
                                        />
                                        <CreatableSelect
                                            isMulti
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                            placeholder={$t('Add email addresses')}
                                            options={memberOptions}
                                            value={(dcRecipients || []).map((email) => ({ label: email, value: email }))}
                                            onChange={(selected) => setDcRecipients((selected || []).map((option) => option.value))}
                                            formatCreateLabel={(input) => `${$t('Add')} "${input}"`}
                                        />
                                    </div>
                                )}
                                <div className="tw-flex tw-gap-x-3">
                                    <Button variant="secondary" disabled={isSubmitting || commitRunning} onClick={handleCancel}>
                                        {$t('Cancel')}
                                    </Button>
                                    {/* Save draft stays enabled even while a supplier request is open (IB-610). */}
                                    <Button variant="secondary" disabled={isSubmitting || commitRunning} onClick={() => handleSaveDraft(values)}>
                                        {$t('Save draft')}
                                    </Button>
                                    <Tooltip
                                        placement="top"
                                        tooltipType="text"
                                        tooltipContent={$t('Total percentage must equal 100')}
                                        allowTooltip={!milestonesValid}
                                    >
                                        <span className="tw-inline-block">
                                            <Button
                                                variant="primary"
                                                className="tw-flex tw-items-center tw-gap-x-2"
                                                loading={commitRunning}
                                                disabled={isSubmitting || commitRunning || submitDisabled || !!supplierRequest}
                                                onClick={handleSubmit}
                                            >
                                                {commitRunning ? $t('Committing...') : props.dcEmailEnabled ? $t('Approve & send') : $t('Direct Commit')}
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </div>
                            </div>
                        </div>
                    );
                }}
            </Formik>
            <UncommittedExpendituresEditModal
                modalId="direct-commit-edit-uncommitted-expenditure"
                modalRef={editModalRef}
                modalElement={editModalElement}
                uncommittedExpenditure={editingLine}
                uncommittedExpendituresUpdateUrl={uncommittedExpendituresUpdateUrl}
                uncommittedExpendituresPreCommitUrl={uncommittedExpendituresPreCommitUrl}
                expenseFundApprovalsUpdateUrl={expenseFundApprovalsUpdateUrl}
                closeHandler={editModalCloseHandler}
                updateSuccessHandler={editModalSuccessHandler}
                department={typeof department !== 'undefined' ? department : undefined}
                costcenter={typeof costcenter !== 'undefined' ? costcenter : undefined}
                isProjectSblExpense={() => isProjectSblExpense(editingLine)}
            />
            <SuppliersCreateModal
                modalId={'create-supplier-modal'}
                modalRef={suppliersCreateModalRef}
                modalElement={suppliersCreateModalElement}
                closeHandler={() => {
                    setCreateNewSupplier(false);
                    suppliersCreateModalElement && suppliersCreateModalElement.hide();
                }}
                successHandler={() => {
                    fetchData();
                    setCreateNewSupplier(false);
                    suppliersCreateModalElement && suppliersCreateModalElement.hide();
                }}
                suppliersIndexUrl={suppliersIndexUrl}
                suppliersStoreUrl={suppliersStoreUrl}
                suppliersCreateUrl={suppliersCreateUrl}
            />
            <RequestSupplierModal
                modalId={'request-supplier-modal'}
                modalRef={requestSupplierModalRef}
                modalElement={requestSupplierModalElement}
                storeUrl={props.supplierRequestStoreUrl}
                requestableType={'App\\UncommittedExpenditureGroup'}
                requestableId={uncommittedExpenditureGroupId}
                recipients={supplierRequestRecipients}
                suggestionsUrl={props.supplierRequestSuggestionsUrl}
                onSelectExisting={(supplier) => setFieldValueRef.current && setFieldValueRef.current('supplier_id', supplier.id)}
                onBeforeRequest={() => handleSaveDraft(latestValuesRef.current, { silent: true })}
                onRequested={(data) => setSupplierRequest(data)}
            />
        </div>
    );
}
